// PanelDoorpanel.js
// Door Panel section for Steel Doors.
// Owns its controlMap; bubbles up the full section data via onChange(sectionData).
// Re-uses existing child controls from src/controls/doorpanel/.

import { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import DoorSearch     from '../../controls/doorpanel/DoorSearch';
import DoorCollection from '../../controls/doorpanel/DoorCollection';
import GrainFilter    from '../../controls/doorpanel/GrainFilter';
import DoorItems      from '../../controls/doorpanel/DoorItems';

const PanelDoorpanel = ({
  data          = {},
  onChange,
  disabled      = false,
  sectionId     = 'DoorPanel',
  state,
  onExpandChange,
  forceExpanded,
  doorSize,    // from frameData.doorSize  e.g. { value: '32"', label: '32"' }
  doorHeight,  // from frameData.doorHeight e.g. { value: "6'8\"", label: "6'8\"" }
}) => {

  // ── section state ─────────────────────────────────────────────────────────
  const [collection,     setCollection]     = useState(data.collection     || '');
  const [grainFilter,    setGrainFilter]    = useState(data.grainFilter    || '');
  const [searchText,     setSearchText]     = useState(data.searchText     || '');
  const [selectedPanel,  setSelectedPanel]  = useState(data.selectedPanel  || null);

  // ── sync when parent loads saved data ─────────────────────────────────────
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    if (!data || Object.keys(data).length === 0) return;
    loadedRef.current = true;
    setCollection(data.collection    || '');
    setGrainFilter(data.grainFilter  || '');
    setSearchText(data.searchText    || '');
    setSelectedPanel(data.selectedPanel || null);
  }, [data]);

  // ── bubble up ─────────────────────────────────────────────────────────────
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    onChange?.({ collection, grainFilter, searchText, selectedPanel });
  }, [collection, grainFilter, searchText, selectedPanel]);

  // ── section title ─────────────────────────────────────────────────────────
  const title = selectedPanel?.value
    ? `Door Panel  (${selectedPanel.value})`
    : 'Door Panel';

  // ── extract string values for DoorItems ───────────────────────────────────
  // DoorItems appends its own " to the value, so strip any trailing " first
  const doorSizeStr   = (doorSize?.value   || '').replace(/"$/, '');
  const doorHeightStr = (doorHeight?.value || '').replace(/"$/, '');

  // ── controlMap — filters only (DoorItems rendered as child to avoid remount) ─
  const controlMap = [
    {
      id: 'DoorSearch',
      component: (
        <DoorSearch
          id="DoorSearch"
          placeholder="Search panels..."
          onChange={setSearchText}
          disabled={disabled}
        />
      ),
      position: [1, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'DoorCollection',
      component: (
        <DoorCollection
          id="DoorCollection"
          value={collection}
          onChange={setCollection}
          disabled={disabled}
        />
      ),
      position: [2, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'GrainFilter',
      component: (
        <GrainFilter
          id="GrainFilter"
          value={grainFilter}
          onChange={setGrainFilter}
          disabled={disabled}
        />
      ),
      position: [2, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
  ];

  return (
    <ExpandableSection
      title={title}
      sectionId={sectionId}
      state={state}
      onExpandChange={onExpandChange}
      forceExpanded={forceExpanded}
      panelLocation="DoorPanel"
      controlMap={controlMap.filter(c => c.expandableSectionId === sectionId)}
    >
      <DoorItems
        id="DoorItems"
        value={selectedPanel}
        onChange={setSelectedPanel}
        selectedCollection={collection}
        selectedGrainFilter={grainFilter}
        doorSizeValueToFunc={doorSizeStr}
        doorHeightValueToFunc={doorHeightStr}
        searchByName={searchText}
      />
    </ExpandableSection>
  );
};

export default PanelDoorpanel;
